import React, { useState } from 'react';
import {
  ShoppingCart,
  Building2,
  Cloud,
  FileText,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Users,
  Clock,
  CreditCard,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Info,
} from 'lucide-react';

type ChannelId = 'ea' | 'csp' | 'sce';

interface Channel {
  id: ChannelId;
  name: string;
  fullName: string;
  icon: React.ReactNode;
  tagline: string;
  color: string;
  bgColor: string;
  borderColor: string;
  bestFor: string[];
  orgSize: string;
  termLength: string;
  billing: string;
  keyBenefits: string[];
  limitations: string[];
  details: string;
}

const channels: Channel[] = [
  {
    id: 'ea',
    name: 'EA',
    fullName: 'Enterprise Agreement',
    icon: <Building2 className="w-6 h-6" />,
    tagline: 'Traditional volume licensing for large organizations',
    color: 'text-slate-700',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    bestFor: [
      'Organizations with 500+ users',
      'Established Microsoft estates with predictable needs',
      'Customers needing on-premises + cloud hybrid rights',
    ],
    orgSize: '500+ users (minimum commitment)',
    termLength: '3-year agreement',
    billing: 'Annual payments, can spread across 3 years',
    keyBenefits: [
      'Price lock for the full 3-year term',
      'On-premises deployment rights (perpetual-use during term)',
      'Software Assurance benefits (training, support, migration)',
      'Flexibility to true-up (add seats) annually',
    ],
    limitations: [
      'Microsoft is actively guiding customers toward CSP/NCE and SCE',
      'Minimum 500-user commitment for most organizations',
      'Less agile — changes typically happen at anniversary',
      'Not available to all customer segments going forward',
    ],
    details:
      'The Enterprise Agreement has been the cornerstone of Microsoft volume licensing since the 1990s. However, Microsoft has been steadily shifting incentives toward cloud-first purchasing models. Many EA customers are being encouraged to transition to CSP (NCE) or the newer SCE model at renewal. If you are on an EA today, plan your next renewal strategy early — your options at renewal may look different than they did 3 years ago.',
  },
  {
    id: 'csp',
    name: 'CSP / NCE',
    fullName: 'Cloud Solution Provider — New Commerce Experience',
    icon: <Cloud className="w-6 h-6" />,
    tagline: 'The modern cloud purchasing model through a Microsoft partner',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    bestFor: [
      'Organizations of any size (no minimum)',
      'Businesses wanting partner-managed billing and support',
      'Teams that need flexibility to scale seats up or down',
    ],
    orgSize: 'No minimum — 1 seat and up',
    termLength: 'Monthly or annual subscriptions',
    billing: 'Monthly or annual billing through your CSP partner',
    keyBenefits: [
      'No minimum seat count — accessible to any organization',
      'Monthly or annual commitment terms for flexibility',
      'Your partner can bundle support, migration, and managed services',
      'Seat count can increase mid-term; annual terms can decrease at renewal',
      'Single invoice from your partner for all Microsoft cloud services',
    ],
    limitations: [
      'Annual commitments cannot be reduced mid-term (only at renewal)',
      'Monthly subscriptions carry a pricing premium (~20% more than annual)',
      'No on-premises perpetual-use rights (cloud only)',
      'Pricing set by your partner — may vary',
    ],
    details:
      'CSP through the New Commerce Experience (NCE) is Microsoft\'s primary go-to-market channel for cloud licensing. Most small and mid-sized businesses purchase here, and Microsoft is actively moving larger customers into this model as well. You buy through a Microsoft partner (like Celebration OnPoint) who handles billing, provisioning, and support. Annual commitment terms offer the best pricing. Monthly terms offer maximum flexibility but at a premium. This is the most common path for new Microsoft 365 customers today.',
  },
  {
    id: 'sce',
    name: 'SCE',
    fullName: 'Subscription Cloud Enrollment',
    icon: <FileText className="w-6 h-6" />,
    tagline: 'Enterprise-scale cloud subscriptions with volume pricing',
    color: 'text-teal-700',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    bestFor: [
      'Large organizations (500+ users) moving fully to cloud',
      'EA customers transitioning at renewal',
      'Organizations wanting enterprise pricing without on-premises rights',
    ],
    orgSize: '500+ users (enterprise scale)',
    termLength: '1-year or 3-year terms',
    billing: 'Annual billing with enterprise-level pricing',
    keyBenefits: [
      'Enterprise-level pricing for cloud-only subscriptions',
      'Designed as the natural successor to EA for cloud workloads',
      'Available through Microsoft directly or through partners',
      'Can coexist with other agreements during transition',
      'Simplified true-up and management for large deployments',
    ],
    limitations: [
      'Cloud-only — no on-premises perpetual-use rights',
      'Minimum seat commitments similar to EA',
      'Relatively new — fewer partners have deep experience',
      'Limited flexibility compared to monthly CSP terms',
    ],
    details:
      'The Subscription Cloud Enrollment is Microsoft\'s newer enterprise licensing vehicle, purpose-built for organizations that are fully committed to the cloud. Think of it as the spiritual successor to the EA for customers who no longer need on-premises deployment rights. If your EA is coming up for renewal and your infrastructure is predominantly cloud-based, SCE is likely where Microsoft will steer the conversation. It offers enterprise-level pricing and the structure of a volume agreement without the on-premises baggage.',
  },
];

const ChannelCard: React.FC<{
  channel: Channel;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ channel, isExpanded, onToggle }) => {
  return (
    <div
      className={`rounded-xl border-2 ${channel.borderColor} overflow-hidden transition-all duration-300 ${
        isExpanded ? 'shadow-lg' : 'shadow-sm hover:shadow-md'
      }`}
    >
      <button
        onClick={onToggle}
        className={`w-full ${channel.bgColor} p-6 text-left transition-colors`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`${channel.color} mt-0.5`}>{channel.icon}</div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className={`text-xl font-bold ${channel.color}`}>
                  {channel.name}
                </h3>
                <span className="text-sm font-medium text-gray-500">
                  {channel.fullName}
                </span>
              </div>
              <p className="text-gray-600 mt-1 text-sm">{channel.tagline}</p>
            </div>
          </div>
          <div className={`${channel.color} flex-shrink-0 ml-4`}>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="p-6 bg-white space-y-6 animate-in fade-in duration-200">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <Users className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Org Size
                </p>
                <p className="text-sm text-gray-800 mt-0.5">
                  {channel.orgSize}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Term Length
                </p>
                <p className="text-sm text-gray-800 mt-0.5">
                  {channel.termLength}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CreditCard className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Billing
                </p>
                <p className="text-sm text-gray-800 mt-0.5">
                  {channel.billing}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">
              Best For
            </h4>
            <ul className="space-y-1.5">
              {channel.bestFor.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm">
                  <ArrowRight className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-green-700 mb-2">
                Key Benefits
              </h4>
              <ul className="space-y-1.5">
                {channel.keyBenefits.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-amber-700 mb-2">
                Limitations
              </h4>
              <ul className="space-y-1.5">
                {channel.limitations.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <XCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <p className="text-sm text-gray-600 leading-relaxed">
              {channel.details}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const HowToBuy: React.FC = () => {
  const [expandedChannel, setExpandedChannel] = useState<ChannelId | null>(
    'csp'
  );

  const toggle = (id: ChannelId) => {
    setExpandedChannel(expandedChannel === id ? null : id);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center gap-3 mb-2">
        <ShoppingCart className="w-6 h-6 text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-900">
          How to Buy Microsoft 365
        </h2>
      </div>
      <p className="text-gray-600 mb-8 max-w-3xl">
        Microsoft offers several purchasing channels, each with different
        pricing, flexibility, and commitment structures. Choosing the right one
        can save your organization significant cost and complexity.
      </p>

      <div className="space-y-4 mb-8">
        {channels.map((channel) => (
          <ChannelCard
            key={channel.id}
            channel={channel}
            isExpanded={expandedChannel === channel.id}
            onToggle={() => toggle(channel.id)}
          />
        ))}
      </div>

      <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-bold text-amber-800 mb-2">
              EA Phase-Out: What You Need to Know
            </h3>
            <p className="text-sm text-amber-900 leading-relaxed mb-3">
              Microsoft is actively transitioning customers away from the
              traditional Enterprise Agreement, particularly for cloud-only
              workloads. While EA renewals are still available, Microsoft
              incentives and partner programs are increasingly aligned with CSP
              (NCE) and SCE. If your EA is renewing in the next 12 months, now
              is the time to evaluate your options.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
                <p className="font-semibold text-amber-800">
                  Small / Mid-Size
                </p>
                <p className="text-amber-700 mt-1">
                  CSP (NCE) is almost always the right fit. Maximum flexibility,
                  no minimums.
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
                <p className="font-semibold text-amber-800">
                  Enterprise (Cloud-First)
                </p>
                <p className="text-amber-700 mt-1">
                  SCE offers enterprise pricing without the on-premises baggage.
                  Ideal for EA renewals.
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
                <p className="font-semibold text-amber-800">
                  Enterprise (Hybrid)
                </p>
                <p className="text-amber-700 mt-1">
                  EA may still make sense if you need on-premises rights, but
                  plan your exit path.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-5">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-800 mb-1">
              Not sure which channel is right for you?
            </h4>
            <p className="text-sm text-blue-700">
              Licensing channel decisions depend on your organization size,
              cloud readiness, existing agreements, and budget cycle. A
              Microsoft licensing partner like{' '}
              <a
                href="https://onpointhq.com/capabilities/pro-services/microsoft-license/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline hover:text-blue-900 transition-colors"
              >
                Celebration OnPoint
              </a>{' '}
              can walk you through the tradeoffs and build a strategy tailored
              to your needs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
